/**
 * @module adapter
 * @package @geenius/adapters/convex
 * @description Provides a typed adapter-side binder for Convex database
 * providers produced by `@geenius/db`. This package performs shape validation
 * and returns a `DbAdapter` wrapper without importing or constructing a
 * Convex runtime client.
 */

import type { DbAdapter, ListOptions, QueryFilter } from "@geenius/adapters";
import { DbError, isAdapterError } from "@geenius/adapters";
import { ConvexBindingError } from "./errors";
import type {
  ConvexDbAdapterBinding,
  ConvexDbAdapterOptions,
  ConvexDbFactoryOptions,
  ConvexProviderContract,
  ConvexTableExpectations,
} from "./types";

const DB_ADAPTER_METHODS = [
  "create",
  "get",
  "update",
  "delete",
  "list",
  "query",
  "count",
] as const satisfies readonly (keyof DbAdapter)[];
const DB_ADAPTER_METHOD_NAMES = new Set<string>(DB_ADAPTER_METHODS);
const CONVEX_SCHEMA_METADATA_KEYS = new Set<string>([
  "schema",
  "fields",
  "indexes",
]);
const CONVEX_SCHEMA_FIELD_DESCRIPTOR_KEYS = new Set<string>([
  "type",
  "fields",
  "items",
  "nullable",
  "optional",
]);

const CONVEX_PROVIDER_CONTRACT = {
  provider: "convex",
  stage: "launch",
  sdk: "convex",
  schema: "ConvexDB schema",
  factory: "createConvexDbAdapter",
} as const satisfies Omit<ConvexProviderContract, "tables">;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasAdapter(
  value: ConvexDbAdapterOptions,
): value is ConvexDbAdapterBinding {
  return isObject(value) && "adapter" in value;
}

function assertConvexProviderTag(config: ConvexDbAdapterOptions): void {
  if (!isObject(config) || !("provider" in config)) {
    return;
  }

  if (config.provider !== undefined && config.provider !== "convex") {
    throw new ConvexBindingError(
      `withConvexDb expected provider "convex" but received "${String(config.provider)}".`,
    );
  }
}

function getCandidate(config: ConvexDbAdapterOptions): DbAdapter | unknown {
  assertConvexProviderTag(config);

  if (hasAdapter(config)) {
    return config.adapter;
  }

  return config;
}

function getFactoryOptions(
  config: ConvexDbAdapterOptions,
): ConvexDbFactoryOptions | undefined {
  if (hasAdapter(config)) {
    return config.source?.factoryOptions;
  }

  return undefined;
}

function createContractFromFactoryOptions(
  factoryOptions: ConvexDbFactoryOptions | undefined,
): ConvexProviderContract | undefined {
  if (!factoryOptions) {
    return undefined;
  }

  const tables =
    factoryOptions.tables ??
    factoryOptions.schema?.tables ??
    factoryOptions.functions;

  if (tables === undefined) {
    return undefined;
  }

  return {
    ...CONVEX_PROVIDER_CONTRACT,
    tables,
  };
}

function getConvexContract(
  config: ConvexDbAdapterOptions,
  adapter: DbAdapter,
): ConvexProviderContract | undefined {
  if (hasAdapter(config) && config.contract !== undefined) {
    if (!isObject(config.contract)) {
      throw new ConvexBindingError(
        "Convex DbAdapter binding contract must be an object.",
      );
    }

    return config.contract as unknown as ConvexProviderContract;
  }

  if (isObject(adapter) && isObject(adapter.convex)) {
    return adapter.convex as unknown as ConvexProviderContract;
  }

  if (isObject(adapter) && isObject(adapter.contract)) {
    return adapter.contract as unknown as ConvexProviderContract;
  }

  if (
    isObject(adapter) &&
    adapter.provider === "convex" &&
    adapter.tables !== undefined
  ) {
    // Verify the factory supplied the full launch metadata at the top level
    // rather than synthesizing stage/sdk/schema/factory locally. Synthesizing
    // would mask factories that drift away from the launch ConvexProviderContract.
    const missing = (["stage", "sdk", "schema", "factory"] as const).filter(
      (key) => {
        const expected = CONVEX_PROVIDER_CONTRACT[key];
        return (adapter as Record<string, unknown>)[key] !== expected;
      },
    );
    if (missing.length > 0) {
      throw new ConvexBindingError(
        `Convex DbAdapter binding is missing required launch metadata: ${missing.join(", ")}. Provide a full ConvexProviderContract via the "convex" or "contract" namespace, or expose stage/sdk/schema/factory at the top level.`,
      );
    }
    return {
      ...CONVEX_PROVIDER_CONTRACT,
      tables: adapter.tables as ConvexTableExpectations,
    };
  }

  return createContractFromFactoryOptions(getFactoryOptions(config));
}

function normalizeTableName(value: unknown, context: string): string {
  if (typeof value !== "string") {
    throw new ConvexBindingError(
      `Convex table ${context} must be a string table name.`,
    );
  }

  const tableName = value.trim();
  if (tableName.length === 0) {
    throw new ConvexBindingError(
      `Convex table ${context} must be a non-empty string.`,
    );
  }

  return tableName;
}

function assertNonEmptyString(value: unknown, context: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new ConvexBindingError(`${context} must be a non-empty string.`);
  }

  return value.trim();
}

function assertNonEmptyStringList(value: unknown, context: string): void {
  if (!Array.isArray(value) || value.length === 0) {
    throw new ConvexBindingError(
      `${context} must be a non-empty array of strings.`,
    );
  }

  for (const [index, entry] of value.entries()) {
    assertNonEmptyString(entry, `${context} entry at index ${index}`);
  }
}

function getTableNames(tables: ConvexTableExpectations): string[] {
  if (Array.isArray(tables)) {
    return tables.map((tableName, index) =>
      normalizeTableName(tableName, `at index ${index}`),
    );
  }

  return Object.keys(tables).map((tableName) =>
    normalizeTableName(tableName, `key "${tableName}"`),
  );
}

const CONVEX_OPERATION_KINDS = new Set<string>(["action", "mutation", "query"]);

function assertConvexOperationDescriptor(
  tableName: string,
  operation: string,
  descriptor: unknown,
): void {
  if (typeof descriptor === "boolean") {
    return;
  }

  if (typeof descriptor === "string") {
    if (descriptor.trim().length === 0) {
      throw new ConvexBindingError(
        `Convex table ${tableName} operation "${operation}" must be a non-empty string when provided as a function reference.`,
      );
    }
    return;
  }

  if (!isObject(descriptor)) {
    throw new ConvexBindingError(
      `Convex table ${tableName} operation "${operation}" must be a boolean, non-empty string, or descriptor object.`,
    );
  }

  if (descriptor.kind !== undefined) {
    if (
      typeof descriptor.kind !== "string" ||
      !CONVEX_OPERATION_KINDS.has(descriptor.kind)
    ) {
      throw new ConvexBindingError(
        `Convex table ${tableName} operation "${operation}" has invalid kind "${String(descriptor.kind)}"; expected one of action | mutation | query.`,
      );
    }
  }

  if (descriptor.name !== undefined) {
    if (
      typeof descriptor.name !== "string" ||
      descriptor.name.trim().length === 0
    ) {
      throw new ConvexBindingError(
        `Convex table ${tableName} operation "${operation}" name must be a non-empty string.`,
      );
    }
  }

  if (descriptor.args !== undefined) {
    if (
      !Array.isArray(descriptor.args) ||
      descriptor.args.some(
        (arg) => typeof arg !== "string" || arg.trim().length === 0,
      )
    ) {
      throw new ConvexBindingError(
        `Convex table ${tableName} operation "${operation}" args must be an array of non-empty strings.`,
      );
    }
  }

  if (descriptor.returns !== undefined) {
    if (
      typeof descriptor.returns !== "string" ||
      descriptor.returns.trim().length === 0
    ) {
      throw new ConvexBindingError(
        `Convex table ${tableName} operation "${operation}" returns must be a non-empty string.`,
      );
    }
  }
}

function assertConvexSchemaFieldMap(
  tableName: string,
  context: string,
  fields: unknown,
  parentPath?: string,
): void {
  if (!isObject(fields)) {
    throw new ConvexBindingError(
      `Convex table ${tableName} ${context} must be a field descriptor object.`,
    );
  }

  const entries = Object.entries(fields);
  if (entries.length === 0) {
    throw new ConvexBindingError(
      `Convex table ${tableName} ${context} must include at least one field.`,
    );
  }

  for (const [fieldName, descriptor] of entries) {
    const normalizedFieldName = assertNonEmptyString(
      fieldName,
      `Convex table ${tableName} ${context} field name`,
    );
    const path =
      parentPath === undefined
        ? normalizedFieldName
        : `${parentPath}.${normalizedFieldName}`;

    assertConvexSchemaFieldExpectation(tableName, path, descriptor);
  }
}

function assertConvexSchemaFieldExpectation(
  tableName: string,
  path: string,
  descriptor: unknown,
): void {
  const context = `Convex table ${tableName} schema field "${path}"`;

  if (typeof descriptor === "string") {
    assertNonEmptyString(descriptor, `${context} type`);
    return;
  }

  if (!isObject(descriptor)) {
    throw new ConvexBindingError(
      `${context} must be a non-empty string or descriptor object.`,
    );
  }

  const unknownKeys = Object.keys(descriptor).filter(
    (key) => !CONVEX_SCHEMA_FIELD_DESCRIPTOR_KEYS.has(key),
  );
  if (unknownKeys.length > 0) {
    throw new ConvexBindingError(
      `${context} has unknown descriptor keys: ${unknownKeys.join(", ")}`,
    );
  }

  assertNonEmptyString(descriptor.type, `${context} type`);

  if (descriptor.fields !== undefined) {
    assertConvexSchemaFieldMap(
      tableName,
      `fields ${path}`,
      descriptor.fields,
      path,
    );
  }

  if (descriptor.items !== undefined) {
    assertConvexSchemaFieldExpectation(
      tableName,
      `${path}[]`,
      descriptor.items,
    );
  }

  if (
    descriptor.nullable !== undefined &&
    typeof descriptor.nullable !== "boolean"
  ) {
    throw new ConvexBindingError(`${context} nullable must be a boolean.`);
  }

  if (
    descriptor.optional !== undefined &&
    typeof descriptor.optional !== "boolean"
  ) {
    throw new ConvexBindingError(`${context} optional must be a boolean.`);
  }
}

function assertConvexIndexes(tableName: string, indexes: unknown): void {
  if (Array.isArray(indexes)) {
    assertNonEmptyStringList(indexes, `Convex table ${tableName} indexes`);
    return;
  }

  if (!isObject(indexes)) {
    throw new ConvexBindingError(
      `Convex table ${tableName} indexes must be an array or descriptor object.`,
    );
  }

  const entries = Object.entries(indexes);
  if (entries.length === 0) {
    throw new ConvexBindingError(
      `Convex table ${tableName} indexes must include at least one index.`,
    );
  }

  for (const [indexName, fields] of entries) {
    const normalizedIndexName = assertNonEmptyString(
      indexName,
      `Convex table ${tableName} index name`,
    );
    assertNonEmptyStringList(
      fields,
      `Convex table ${tableName} index "${normalizedIndexName}" fields`,
    );
  }
}

function assertConvexSchemaMetadata(
  tableName: string,
  expectation: Record<string, unknown>,
): void {
  if ("schema" in expectation) {
    const { schema } = expectation;

    if (typeof schema === "string") {
      assertNonEmptyString(schema, `Convex table ${tableName} schema`);
    } else {
      assertConvexSchemaFieldMap(tableName, "schema", schema);
    }
  }

  if ("fields" in expectation) {
    assertConvexSchemaFieldMap(tableName, "fields", expectation.fields);
  }

  if ("indexes" in expectation) {
    assertConvexIndexes(tableName, expectation.indexes);
  }
}

function assertConvexCollectionExpectations(
  tables: ConvexTableExpectations,
): void {
  if (Array.isArray(tables)) {
    return;
  }

  for (const [tableName, expectation] of Object.entries(tables)) {
    if (!isObject(expectation)) {
      throw new ConvexBindingError(
        `Convex table ${tableName} must include schema metadata or operation expectations.`,
      );
    }

    const hasOperationExpectation = Object.keys(expectation).some((key) =>
      DB_ADAPTER_METHOD_NAMES.has(key),
    );
    const hasSchemaMetadata = Object.keys(expectation).some((key) =>
      CONVEX_SCHEMA_METADATA_KEYS.has(key),
    );

    if (!hasOperationExpectation && !hasSchemaMetadata) {
      throw new ConvexBindingError(
        `Convex table ${tableName} must include schema metadata or operation expectations.`,
      );
    }

    if (hasSchemaMetadata) {
      assertConvexSchemaMetadata(
        tableName,
        expectation as Record<string, unknown>,
      );
    }

    if (hasSchemaMetadata && !hasOperationExpectation) {
      continue;
    }

    const missingOperations = DB_ADAPTER_METHODS.filter(
      (operation) => !(operation in expectation),
    );

    if (missingOperations.length > 0) {
      throw new ConvexBindingError(
        `Convex table ${tableName} is missing operation expectations: ${missingOperations.join(", ")}`,
      );
    }

    for (const operation of DB_ADAPTER_METHODS) {
      assertConvexOperationDescriptor(
        tableName,
        operation,
        (expectation as Record<string, unknown>)[operation],
      );
    }
  }
}

function assertConvexProviderContract(
  config: ConvexDbAdapterOptions,
  adapter: DbAdapter,
): ReadonlySet<string> {
  const contract = getConvexContract(config, adapter);

  if (contract === undefined) {
    throw new ConvexBindingError(
      "withConvexDb requires Convex launch metadata and schema/table expectations; generic DbAdapters are not accepted.",
    );
  }

  const expectedEntries = Object.entries(CONVEX_PROVIDER_CONTRACT);
  const mismatches = expectedEntries
    .filter(([key, expectedValue]) => {
      const actual = contract[key as keyof typeof CONVEX_PROVIDER_CONTRACT];
      return actual !== expectedValue;
    })
    .map(
      ([key, expectedValue]) =>
        `${key}=${String(expectedValue)} (received ${String(
          contract[key as keyof typeof CONVEX_PROVIDER_CONTRACT],
        )})`,
    );

  if (mismatches.length > 0) {
    throw new ConvexBindingError(
      `Convex DbAdapter binding has invalid provider metadata: ${mismatches.join(", ")}`,
    );
  }

  if (
    !Array.isArray(contract.tables) &&
    (!isObject(contract.tables) || Array.isArray(contract.tables))
  ) {
    throw new ConvexBindingError(
      "Convex DbAdapter binding requires a table expectation array or registry object.",
    );
  }

  const tableNames = getTableNames(contract.tables);

  if (tableNames.length === 0) {
    throw new ConvexBindingError(
      "Convex DbAdapter binding requires at least one schema/table expectation.",
    );
  }

  assertConvexCollectionExpectations(contract.tables);
  return new Set(tableNames);
}

function assertDbAdapter(value: unknown): asserts value is DbAdapter {
  if (!isObject(value)) {
    throw new ConvexBindingError(
      "withConvexDb expected a @geenius/db Convex DbAdapter factory output.",
    );
  }

  const missingMethods = DB_ADAPTER_METHODS.filter(
    (method) => typeof value[method] !== "function",
  );

  if (missingMethods.length > 0) {
    throw new ConvexBindingError(
      `Convex DbAdapter binding is missing methods: ${missingMethods.join(", ")}`,
    );
  }
}

function assertDeclaredCollection(
  collection: string,
  declaredCollections: ReadonlySet<string>,
): void {
  if (!declaredCollections.has(collection)) {
    throw new DbError(
      `Convex collection "${collection}" is not declared in the provider contract tables.`,
      "COLLECTION_NOT_CONFIGURED",
    );
  }
}

async function delegateConvexOperation<T>(
  operation: (typeof DB_ADAPTER_METHODS)[number],
  delegate: () => Promise<T>,
): Promise<T> {
  try {
    return await delegate();
  } catch (error) {
    if (isAdapterError(error)) {
      throw error;
    }

    throw new DbError(
      `Convex ${operation} operation failed`,
      "QUERY_ERROR",
      error,
    );
  }
}

/**
 * Wraps a Convex-compatible DB adapter in the shared adapters contract.
 *
 * @param config Existing DB adapter output with Convex contract metadata, or a
 * binding object tagged for Convex. Downstream `@geenius/db` factories may
 * provide metadata through `convex`, `contract`, or top-level
 * `provider: "convex"` plus `tables`.
 * @returns A DB adapter that delegates CRUD, list, query, and count operations.
 */
export function withConvexDb(config: ConvexDbAdapterOptions): DbAdapter {
  const adapter = getCandidate(config);

  assertDbAdapter(adapter);
  const declaredCollections = assertConvexProviderContract(config, adapter);

  return {
    async create<T extends Record<string, unknown>>(
      collection: string,
      data: Omit<T, "id">,
    ): Promise<T & { id: string }> {
      assertDeclaredCollection(collection, declaredCollections);
      return delegateConvexOperation("create", () =>
        adapter.create<T>(collection, data),
      );
    },

    async get<T>(collection: string, id: string): Promise<T | null> {
      assertDeclaredCollection(collection, declaredCollections);
      return delegateConvexOperation("get", () =>
        adapter.get<T>(collection, id),
      );
    },

    async update<T extends Record<string, unknown>>(
      collection: string,
      id: string,
      data: Partial<T>,
    ): Promise<T | null> {
      assertDeclaredCollection(collection, declaredCollections);
      return delegateConvexOperation("update", () =>
        adapter.update<T>(collection, id, data),
      );
    },

    async delete(collection: string, id: string): Promise<boolean> {
      assertDeclaredCollection(collection, declaredCollections);
      return delegateConvexOperation("delete", () =>
        adapter.delete(collection, id),
      );
    },

    async list<T>(collection: string, options?: ListOptions): Promise<T[]> {
      assertDeclaredCollection(collection, declaredCollections);
      return delegateConvexOperation("list", () =>
        adapter.list<T>(collection, options),
      );
    },

    async query<T>(collection: string, filter: QueryFilter): Promise<T[]> {
      assertDeclaredCollection(collection, declaredCollections);
      return delegateConvexOperation("query", () =>
        adapter.query<T>(collection, filter),
      );
    },

    async count(collection: string, filter?: QueryFilter): Promise<number> {
      assertDeclaredCollection(collection, declaredCollections);
      return delegateConvexOperation("count", () =>
        adapter.count(collection, filter),
      );
    },
  };
}
